import React, { useEffect, useMemo } from 'react';
import {
  Row, Col, Card, Statistic, Tag, Space, Typography, List,
  Badge, Button, Empty, Progress, Timeline, Divider,
} from 'antd';
import {
  TeamOutlined, CalendarOutlined, CheckCircleOutlined,
  WarningOutlined, BankOutlined,
  SoundOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { isScheduledStatus } from './board/boardTypes';
import { useCondoStore } from '../store/condoStore';
import { useAuthStore } from '../store/authStore';
import type {
  BoardMeeting, BoardResolution, BoardMember,
  Announcement, Violation,
} from '../types';

dayjs.extend(isSameOrAfter);

const { Title, Text } = Typography;

type MeetingType = 'agm' | 'board' | 'special' | 'committee';
const MEETING_TYPE_LABELS: Record<MeetingType, string> = {
  agm:       'Annual General Meeting',
  board:     'Board Meeting',
  special:   'Special Meeting',
  committee: 'Committee Meeting',
};
const MEETING_TYPE_COLORS: Record<MeetingType, string> = {
  agm: 'blue', board: 'purple', special: 'orange', committee: 'cyan',
};

/**
 * S16 — Board Member Portal
 * A read-oriented landing page for board-member role users.
 * Shows KPIs, upcoming meetings, open resolutions (with voting),
 * recent announcements, and a violation summary.
 */
export const BoardMemberPortal: React.FC = () => {
  const {
    boardMembers, boardMeetings, boardResolutions,
    fetchBoardMembers, fetchBoardMeetings, fetchBoardResolutions,
    castResolutionVote, closeBoardResolution,
    announcements, fetchAnnouncements,
    violations, fetchViolations,
  } = useCondoStore() as any;
  const { currentCondo } = useAuthStore();

  useEffect(() => {
    if (!currentCondo?.id) return;
    fetchBoardMembers(currentCondo.id);
    fetchBoardMeetings(currentCondo.id);
    fetchBoardResolutions(currentCondo.id);
    fetchAnnouncements(currentCondo.id);
    fetchViolations(currentCondo.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetch actions are stable zustand actions
  }, [currentCondo?.id]);

  // ── Derived data ─────────────────────────────────────────────────────────────
  const upcomingMeetings: BoardMeeting[] = useMemo(
    () =>
      (boardMeetings as BoardMeeting[])
        .filter(
          m =>
            // R11-7 — the backend persists UPPERCASE statuses, so the old
            // case-sensitive `m.status === 'scheduled'` dropped every meeting
            // and this KPI disagreed with Board Management (which uses the
            // case-insensitive helper).
            isScheduledStatus(m.status) &&
            dayjs(m.date).isSameOrAfter(dayjs(), 'day'),
        )
        .sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix())
        .slice(0, 5),
    [boardMeetings],
  );

  const openResolutions: BoardResolution[] = useMemo(
    () => (boardResolutions as BoardResolution[]).filter(r => r.status === 'open'),
    [boardResolutions],
  );

  const recentAnnouncements: Announcement[] = useMemo(
    () =>
      (announcements as Announcement[])
        .filter(a => a.status === 'PUBLISHED')
        .sort((a, b) => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix())
        .slice(0, 5),
    [announcements],
  );

  const activeViolations: Violation[] = useMemo(
    () =>
      (violations as Violation[]).filter(
        v => v.status === 'open' || v.status === 'notice_sent' || v.status === 'escalated',
      ),
    [violations],
  );

  const handleCastVote = async (
    resolution: BoardResolution,
    vote: 'FOR' | 'AGAINST' | 'ABSTAIN',
  ) => {
    try {
      await castResolutionVote(resolution.id, vote);
    } catch { /* handled in store */ }
  };

  const handleCloseVote = async (resolution: BoardResolution) => {
    try {
      await closeBoardResolution(resolution.id);
    } catch { /* handled in store */ }
  };

  // ── KPI calculations ─────────────────────────────────────────────────────────
  const activeMembers = (boardMembers as BoardMember[]).filter(m => m.isActive).length;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          <BankOutlined style={{ marginRight: 10, color: '#2f54eb' }} />
          Board Member Portal
        </Title>
        <Text type="secondary">
          {currentCondo?.name} — {dayjs().format('MMMM D, YYYY')}
        </Text>
      </div>

      {/* KPI Strip */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ background: '#f0f5ff', border: '1px solid #d6e4ff' }}>
            <Statistic
              title="Active Board Members"
              value={activeMembers}
              prefix={<TeamOutlined style={{ color: '#2f54eb' }} />}
              valueStyle={{ color: '#2f54eb' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}>
            <Statistic
              title="Upcoming Meetings"
              value={upcomingMeetings.length}
              prefix={<CalendarOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            bordered={false}
            style={{
              background: openResolutions.length > 0 ? '#fff7e6' : '#f6ffed',
              border: `1px solid ${openResolutions.length > 0 ? '#ffd591' : '#b7eb8f'}`,
            }}
          >
            <Statistic
              title="Open Resolutions"
              value={openResolutions.length}
              prefix={<CheckCircleOutlined style={{ color: openResolutions.length > 0 ? '#fa8c16' : '#52c41a' }} />}
              valueStyle={{ color: openResolutions.length > 0 ? '#fa8c16' : '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            bordered={false}
            style={{
              background: activeViolations.length > 0 ? '#fff1f0' : '#f6ffed',
              border: `1px solid ${activeViolations.length > 0 ? '#ffa39e' : '#b7eb8f'}`,
            }}
          >
            <Statistic
              title="Active Violations"
              value={activeViolations.length}
              prefix={<WarningOutlined style={{ color: activeViolations.length > 0 ? '#f5222d' : '#52c41a' }} />}
              valueStyle={{ color: activeViolations.length > 0 ? '#f5222d' : '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Upcoming Meetings */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <CalendarOutlined style={{ color: '#2f54eb' }} />
                <span>Upcoming Meetings</span>
                {upcomingMeetings.length > 0 && (
                  <Badge count={upcomingMeetings.length} style={{ backgroundColor: '#2f54eb' }} />
                )}
              </Space>
            }
            bordered={false}
            style={{ height: '100%' }}
          >
            {upcomingMeetings.length === 0 ? (
              <Empty description="No upcoming meetings scheduled" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Timeline>
                {upcomingMeetings.map(m => (
                  <Timeline.Item
                    key={m.id}
                    color={MEETING_TYPE_COLORS[m.type as MeetingType] ?? 'blue'}
                    dot={<ClockCircleOutlined />}
                  >
                    <Space direction="vertical" size={2}>
                      <Space>
                        <Text strong>{m.title}</Text>
                        <Tag color={MEETING_TYPE_COLORS[m.type as MeetingType]}>
                          {MEETING_TYPE_LABELS[m.type as MeetingType] ?? m.type}
                        </Tag>
                      </Space>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {dayjs(m.date).format('ddd, MMM D, YYYY')} at {m.startTime}
                        {m.location ? ` · ${m.location}` : ''}
                      </Text>
                    </Space>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </Card>
        </Col>

        {/* Open Resolutions */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <CheckCircleOutlined style={{ color: '#fa8c16' }} />
                <span>Open Resolutions</span>
                {openResolutions.length > 0 && (
                  <Badge count={openResolutions.length} style={{ backgroundColor: '#fa8c16' }} />
                )}
              </Space>
            }
            bordered={false}
            style={{ height: '100%' }}
          >
            {openResolutions.length === 0 ? (
              <Empty description="No resolutions currently open for voting" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List
                dataSource={openResolutions}
                rowKey="id"
                renderItem={(r: BoardResolution) => {
                  const total = r.votesFor + r.votesAgainst + r.votesAbstain;
                  const pct = total > 0 ? Math.round((r.votesFor / total) * 100) : 0;
                  return (
                    <List.Item style={{ flexDirection: 'column', alignItems: 'flex-start', padding: '12px 0' }}>
                      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Space>
                          <Tag color="blue">{r.voteNumber}</Tag>
                          <Text strong>{r.title}</Text>
                        </Space>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          Closes {dayjs(r.closeDate).format('MMM D')}
                        </Text>
                      </Space>
                      {r.description && (
                        <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
                          {r.description}
                        </Text>
                      )}
                      <div style={{ width: '100%', marginBottom: 8 }}>
                        <Progress
                          percent={pct}
                          size="small"
                          strokeColor={pct >= 50 ? '#52c41a' : '#ff4d4f'}
                          format={() => `${r.votesFor}✓ ${r.votesAgainst}✗ ${r.votesAbstain}~`}
                        />
                      </div>
                      <Space size={6}>
                        <Button
                          size="small"
                          type="primary"
                          style={{ background: '#52c41a', borderColor: '#52c41a' }}
                          onClick={() => handleCastVote(r, 'FOR')}
                        >
                          For
                        </Button>
                        <Button size="small" danger onClick={() => handleCastVote(r, 'AGAINST')}>
                          Against
                        </Button>
                        <Button size="small" onClick={() => handleCastVote(r, 'ABSTAIN')}>
                          Abstain
                        </Button>
                        <Button
                          size="small"
                          type="primary"
                          icon={<CheckCircleOutlined />}
                          onClick={() => handleCloseVote(r)}
                        >
                          Close & Tally
                        </Button>
                      </Space>
                    </List.Item>
                  );
                }}
              />
            )}
          </Card>
        </Col>

        {/* Recent Announcements */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <SoundOutlined style={{ color: '#722ed1' }} />
                <span>Recent Announcements</span>
              </Space>
            }
            bordered={false}
          >
            {recentAnnouncements.length === 0 ? (
              <Empty description="No announcements yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List
                dataSource={recentAnnouncements}
                rowKey="id"
                renderItem={(a: Announcement) => (
                  <List.Item style={{ padding: '8px 0' }}>
                    <List.Item.Meta
                      avatar={<SoundOutlined style={{ fontSize: 18, color: '#722ed1', marginTop: 4 }} />}
                      title={<Text strong style={{ fontSize: 13 }}>{a.title}</Text>}
                      description={
                        <Space direction="vertical" size={0}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {dayjs(a.createdAt).format('MMM D, YYYY')}
                            {a.targetAudience ? ` · ${a.targetAudience}` : ''}
                          </Text>
                          {a.content && (
                            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                              {a.content}
                            </Text>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        {/* Active Violations Summary */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <WarningOutlined style={{ color: '#f5222d' }} />
                <span>Active Violations</span>
                {activeViolations.length > 0 && (
                  <Badge count={activeViolations.length} />
                )}
              </Space>
            }
            bordered={false}
          >
            {activeViolations.length === 0 ? (
              <Empty description="No active violations" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List
                dataSource={activeViolations.slice(0, 6)}
                rowKey="id"
                renderItem={(v: Violation) => (
                  <List.Item style={{ padding: '8px 0' }}>
                    <List.Item.Meta
                      avatar={<WarningOutlined style={{ fontSize: 16, color: '#fa8c16', marginTop: 4 }} />}
                      title={
                        <Space>
                          <Text strong style={{ fontSize: 13 }}>{v.category}</Text>
                          <Tag color={v.status === 'escalated' ? 'red' : v.status === 'notice_sent' ? 'orange' : 'gold'}>
                            {v.status.replace('_', ' ').toUpperCase()}
                          </Tag>
                        </Space>
                      }
                      description={
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Unit {v.unitNumber}
                          {v.reportedDate ? ` · ${dayjs(v.reportedDate).format('MMM D, YYYY')}` : ''}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
            {activeViolations.length > 6 && (
              <Divider style={{ margin: '8px 0' }} />
            )}
            {activeViolations.length > 6 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                +{activeViolations.length - 6} more violations
              </Text>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default BoardMemberPortal;
